import { Nullable, createGuid, hasValue, hasValueAndNotEmpty } from "@itk/typescript-utils";
import { ReactElement, useCallback, useMemo } from "react";
import { ChangePasswordFields, LoginFlowProps } from "./login-flow-types";
import { handleOnFieldChange, OptionalFormController, OptionalFormInclude, required, useForm } from "@itk/react-form";
import { Box, Checkbox, FormControlLabel, Stack, Typography, useTheme } from "@mui/material";
import { Button, ITKDataTestAttribute, TooltipShortcut } from "@itk/mui-components";
import { isEqual, password } from "src/utils/validators";
import { LoginFlowStateInputResolver } from "./login-flow-state-input-resolver";
import { RootState } from "src/store";
import { authorizationGateActions } from "../../authorization-gate-store";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CONSTANTS } from "src/utils/constants";
import { loginPageK, useLoginPageT } from "src/translation-hooks/login-page";
import { loginWithOtpPageK, useLoginWithOtpPageT } from "src/translation-hooks/login-with-otp-page";
import { resetPasswordPageK, useResetPasswordPageT } from "src/translation-hooks/reset-password-page";
import { newPasswordPageK, useNewPasswordPageT } from "src/translation-hooks/new-password-page";
import { profileInfoPageK, useProfileInfoPageT } from "src/translation-hooks/profile-info-page";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { passwordValidationCheckmarks } from "src/utils/password-utils";
import { useValidationMessageTranslationsT } from "src/translation-hooks/validation-message-translations";
import { toastActions as itkToastActions } from "src/components/itk-toastr/itk-toastr-store";

const mapStateToProps = (state: RootState) => ({
  loginFlowState: state.generalReducer.authorizationGateReducer.loginFlowState,
  isSubmitting: state.generalReducer.authorizationGateReducer.isSubmitting
});

const ownActions = {
  setLoginFlowState: authorizationGateActions.setLoginFlowState,
  validateOtpRequest: authorizationGateActions.validateOtpRequest,
  sendLoginRequestViaEmail: authorizationGateActions.sendLoginRequestViaEmail,
  changePasswordRequest: authorizationGateActions.changePasswordRequest,
  setLoginState: authorizationGateActions.setLoginState,
  updateUserProfileRequest: authorizationGateActions.updateUserProfileRequest,
  clear: authorizationGateActions.clear,
  addAlert: itkToastActions.addAlert
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps> & LoginFlowProps;

const LoginFlowBase = (props: Props) => {
  const {
    performLogin,
    validateUser,
    validateOtp,
    changePassword,
    loginFlowState,
    requestId,
    loginUsername,
    rememberMe,
    setRememberMe,
    loginState,
    setLoginFlowState,
    validateOtpRequest,
    sendLoginRequestViaEmail,
    changePasswordRequest,
    setLoginState,
    updateUserProfileRequest,
    isSubmitting,
    loginForm,
    addAlert
  } = props;
  const theme = useTheme();
  const navigate = useNavigate();

  const loginPageT = useLoginPageT();
  const loginWithOtpPageT = useLoginWithOtpPageT();
  const resetPasswordPageT = useResetPasswordPageT();
  const newPasswordPageT = useNewPasswordPageT();
  const profileInfoPageT = useProfileInfoPageT();
  const genericTranslationsT = useGenericTranslationsT();
  const validationMessageTranslationsT = useValidationMessageTranslationsT();

  const { validate, getValues } = loginForm;

  const initialPasswordFields = useMemo((): Nullable<ChangePasswordFields> => ({ password: null, repeatPassword: null }), []);
  const initialPasswordFormController = useMemo(
    (): OptionalFormController<ChangePasswordFields> => ({
      password: { validators: [required, password] },
      repeatPassword: { validators: state => [required, isEqual(state.password.value, resetPasswordPageT.resetPassword)] }
    }),
    [resetPasswordPageT, genericTranslationsT.confirmPassword]
  );
  const initialPasswordInclude = useMemo((): OptionalFormInclude<ChangePasswordFields> => ({ password: true, repeatPassword: true }), []);
  const changePasswordForm = useForm(initialPasswordFields, initialPasswordFormController, initialPasswordInclude);
  const { validate: changePasswordValidate, getValues: changePasswordGetValues } = changePasswordForm;

  const handleOtpSubmit = useCallback(() => {
    if (loginState.currentState === "loading") return;
    //Call OTP Validtion api here... then set new-password

    const errors = validate();
    if (hasValue(errors)) return;
    const values = getValues();
    if (values?.oneTimeKey) {
      validateOtpRequest(requestId, String(values.oneTimeKey), isSuccess => {
        if (!isSuccess) handleOnFieldChange(null, loginForm.formController.oneTimeKey);
      });
    }
  }, [getValues, validate]);

  const handleSendCodeAgain = useCallback(
    (email: string) => {
      handleOnFieldChange(null, loginForm.formController.oneTimeKey);
      sendLoginRequestViaEmail(email, createGuid());
      addAlert(loginWithOtpPageT.otpSentSuccessfullyCheckEmail, "success");
    },
    [loginForm]
  );

  type Ttranslation = typeof loginPageT & typeof loginWithOtpPageT & typeof resetPasswordPageT & typeof newPasswordPageT & typeof profileInfoPageT;

  const handleLogin = () => {
    const errors = validate();

    if (hasValue(errors)) return;

    const values = getValues();
    if (values?.email && values?.password && performLogin) {
      performLogin(values.email, values.password);
    }
  };

  const nextStep = useMemo((): {
    onClick?: () => void;
    translation?: ITKDataTestAttribute<Partial<Ttranslation>, keyof Ttranslation>;
    disabled?: boolean;
    leftSideContent?: ReactElement;
    rightSideContent?: ReactElement;
  } => {
    switch (loginFlowState.current) {
      case "login":
        return {
          leftSideContent: (
            <FormControlLabel
              sx={{ paddingLeft: 1, color: theme.palette.customColors.darkGrey }}
              label={loginPageT.rememberMe}
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(_, checked) => setRememberMe(checked)}
                  onKeyUp={e => {
                    if (e.key === "Enter") {
                      handleLogin();
                    }
                  }}
                />
              }
            />
          ),
          rightSideContent: (
            <Typography
              variant="h4"
              color="primary"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => {
                loginForm.resetChanges();
                navigate(`${location.pathname}?type=${CONSTANTS.AUTH_PAGE_QUERY_TYPE.RESET_PASSWORD}`);
              }}
            >
              {resetPasswordPageT.resetPassword}
            </Typography>
          ),
          onClick: () => {
            handleLogin();
          },
          translation: { t: loginPageT, field: "login", k: loginPageK },
          disabled: loginState.currentState === "loading"
        };

      case "otp":
        return {
          leftSideContent: (
            <TooltipShortcut
              shortcut={{
                action: () => handleSendCodeAgain(loginFlowState.emailAddress ?? ""),
                shortCutKey: "g",
                modifier: "alt"
              }}
            >
              <Button variant="text" t={loginWithOtpPageT} field="sendCodeAgain" k={loginWithOtpPageK} onClick={() => handleSendCodeAgain(loginFlowState.emailAddress ?? "")} />
            </TooltipShortcut>
          ),
          onClick: () => {
            handleOtpSubmit();
          },
          disabled: loginState.currentState === "loading"
        };
      case "new-password": {
        if (loginFlowState.stage === "new-user" || loginFlowState.stage === "reset-password") {
          return {
            onClick: () => {
              const errors = validate();
              const values = getValues();
              const isValidPassword = passwordValidationCheckmarks(values?.password || "", validationMessageTranslationsT).every(x => x.checked === true);
              if (hasValue(errors) || !isValidPassword) return;

              if (hasValue(values) && hasValueAndNotEmpty(loginUsername) && hasValueAndNotEmpty(values.password)) {
                changePasswordRequest(loginUsername, values.password);
              }
            },
            translation: { t: newPasswordPageT, field: "createPassword", k: newPasswordPageK },
            disabled: !loginForm.getValues()?.password || isSubmitting
          };
        }
        return {
          onClick: () => {
            const errors = validate();

            if (hasValue(errors)) {
              return;
            } else {
              setLoginFlowState({ current: "completion", stage: "password-reset-success" });
            }
          },
          translation: { t: resetPasswordPageT, k: resetPasswordPageK, field: "resetPassword" }
        };
      }
      case "profile-info": {
        return {
          onClick: () => {
            const errors = validate();
            const values = getValues();

            if (hasValue(errors)) return;

            if (hasValue(values) && hasValue(values.fullName)) {
              updateUserProfileRequest({
                fullName: values.fullName,
                mobilePhoneCountryId: values.mobilePhoneCountryId,
                mobilePhoneNo: values.mobilePhoneNo
              });
            }
          },
          translation: { t: profileInfoPageT, k: profileInfoPageK, field: "completeCreation" },
          disabled: !loginForm.getValues()?.fullName || isSubmitting
        };
      }
      case "forgot-password": {
        if (loginFlowState.stage === "code") {
          return {
            onClick: () => {
              const errors = validate();
              if (hasValue(errors)) {
                return;
              }

              const values = getValues();
              if (!hasValue(values)) {
                return;
              }
              if (hasValueAndNotEmpty(values.oneTimeKey)) {
                handleOtpSubmit();
              }
            },
            leftSideContent: (
              <TooltipShortcut
                shortcut={{
                  action: () => handleSendCodeAgain(loginForm.getValues()?.email || ""),
                  shortCutKey: "g",
                  modifier: "alt"
                }}
              >
                <Button variant="text" t={loginWithOtpPageT} field="sendCodeAgain" k={loginWithOtpPageK} onClick={() => handleSendCodeAgain(loginForm.getValues()?.email || "")} />
              </TooltipShortcut>
            )
          };
        }
        return {
          onClick: () => {
            const errors = validate();
            if (hasValue(errors)) {
              return;
            }

            const values = getValues();
            if (!hasValue(values)) {
              return;
            }
            if (hasValueAndNotEmpty(values.email)) {
              validateUser(values.email.trim(), createGuid(), true);
            }
          },
          translation: { t: resetPasswordPageT, k: resetPasswordPageK, field: "resetPassword" },
          disabled: !loginForm.getValues()?.email || loginState.currentState === "loading"
        };
      }
      case "completion": {
        if (loginFlowState.stage === "new-user")
          return {
            onClick: () => {
              setLoginFlowState({ current: "login" });
              setLoginState({ currentState: "should login" });
              navigate("/");
            },
            translation: { t: loginPageT, k: loginPageK, field: "login" }
          };
        if (loginFlowState.stage === "password-reset-success") {
          return {
            onClick: () => {
              setLoginState({ currentState: "should login" });
              setLoginFlowState({ current: "login" });
              navigate("/");
            },
            translation: { t: loginPageT, k: loginPageK, field: "login" }
          };
        }
        if (loginFlowState.stage === "reset-email-sent") {
          return {
            leftSideContent: (
              <Typography variant="h4" color={theme.palette.primary.main} sx={{ textDecoration: "underline", cursor: "pointer" }}>
                {resetPasswordPageT.sendLinkAgain}
              </Typography>
            ),
            translation: { t: loginPageT, k: loginPageK, field: "login" }
          };
        }
      }
    }

    return {
      onClick: () => {
        const errors = validate();
        if (hasValue(errors)) {
          return;
        }

        const values = getValues();
        if (!hasValue(values)) {
          return;
        }
        if (hasValueAndNotEmpty(values.email) && hasValueAndNotEmpty(values.password)) {
          performLogin(values.email.trim(), values.password);
        }
      },

      translation: { t: loginPageT, field: "login", k: loginPageK }
    };
  }, [
    loginFlowState,
    loginState,
    handleOtpSubmit,
    validate,
    getValues,
    performLogin,
    validateUser,
    validateOtp,
    requestId,
    changePasswordValidate,
    changePasswordGetValues,
    changePassword,
    loginPageT,
    rememberMe,
    sendLoginRequestViaEmail,
    setLoginState,
    loginUsername,
    updateUserProfileRequest,
    validationMessageTranslationsT
  ]);

  return (
    <Stack gap={2} sx={{ justifyContent: "center" }} width="100%">
      <Stack gap={2}>
        <LoginFlowStateInputResolver onclick={nextStep.onClick} state={loginFlowState} form={loginForm} changePasswordForm={changePasswordForm} loginState={loginState} />
      </Stack>
      <Stack direction="row" gap={2} justifyContent="space-between">
        <Box>{nextStep.leftSideContent}</Box>
        <Stack gap={2} pt={1} alignItems="flex-end">
          {nextStep.rightSideContent}
          <Box>
            {nextStep.onClick && nextStep.translation && (
              <TooltipShortcut
                shortcut={{
                  action: nextStep.onClick,
                  shortCutKey: "g",
                  modifier: "alt"
                }}
              >
                <Button
                  variant="contained"
                  onClick={nextStep.onClick}
                  disabled={!!nextStep.disabled}
                  sx={{ fontFamily: "sen", fontSize: theme.typography.body2.fontSize, fontWeight: theme.typography.body2.fontWeight }}
                  {...nextStep.translation}
                />
              </TooltipShortcut>
            )}
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};

export const LoginFlow = connect(mapStateToProps, ownActions)(LoginFlowBase);
